import { OrganisationResponse } from 'api';
import {
  useContext,
  createContext,
  useMemo,
  useReducer,
  useCallback,
} from 'react';
import { useLocation } from 'react-router';
import { Resource } from 'utils/isPathActive';

export type SearchState<FilterType extends string> = {
  resource?: Resource;
  searchTerm?: string;
  category?: string;
  filter?: FilterType;
  checkbox?: boolean;
  organisation?: OrganisationResponse | null;
  sortBy?: string;
};

export const SearchContext = createContext<
  | {
      searchState: SearchState<any>;
      resetState: () => void;
      setSearchState: React.Dispatch<Partial<SearchState<any>>>;
    }
  | undefined
>(undefined);

export const useSearchContext = <FilterType extends string = any>(
  filters?: readonly FilterType[],
) => {
  const context = useContext(
    SearchContext as React.Context<
      | {
          searchState: SearchState<FilterType>;
          resetState: () => void;
          setSearchState: React.Dispatch<Partial<SearchState<FilterType>>>;
        }
      | undefined
    >,
  );
  if (
    filters &&
    context?.searchState.filter &&
    !filters?.includes(context.searchState.filter)
  ) {
    // Ensure that filters are set to valid values for the current SearchFilter, and not one from another page
    context.searchState.filter = filters[0];
  }

  if (context === undefined) {
    throw Error(
      'context was undefined. Please ensure useSearchContext is used within a SearchProvider.',
    );
  }

  return context;
};

export const SearchProvider = <FilterType extends string>({
  children,
  overwrite,
}: {
  children: React.ReactNode;
  overwrite?: {
    searchState: Partial<SearchState<FilterType>>;
    setSearchState: React.Dispatch<Partial<SearchState<FilterType>>>;
  };
}) => {
  const location = useLocation();
  const [searchState, setSearchState] = useReducer(
    (
      data: SearchState<FilterType>,
      partialData: Partial<SearchState<FilterType>>,
    ) => ({
      ...data,
      ...partialData,
    }),
    {
      resource: location?.state?.activeTab,
      searchTerm: '',
      organisation: location?.state?.organisation ?? null,
    },
  );
  const resetState = useCallback(() => {
    setSearchState(
      overwrite?.searchState
        ? overwrite.searchState
        : {
            resource: undefined,
            searchTerm: undefined,
            category: undefined,
            filter: undefined,
            organisation: undefined,
            sortBy: undefined,
          },
    );
  }, [overwrite]);
  const value = useMemo(
    () => ({
      searchState,
      resetState,
      setSearchState,
      ...overwrite,
    }),
    [overwrite, searchState, resetState],
  );
  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};
